import {useEffect, useState} from 'react';
import {
    Button,
    Group,
    Select,
    Stack,
    TextInput,
} from '@mantine/core';
import {PlatformDrawer, PlatformDrawerProps} from '@flexinet/ui-components';
import {
    MdCancel,
    MdCheck,
    MdClear,
} from 'react-icons/md';

import {useApplicationScreenshotsFilter} from '../hooks/useApplicationScreenshotsFilter';
import {ScreenshotStatus} from '../types';

// Only the two fields ApplicationScreenshotsFilterInput accepts (url, status).
// Nothing else may be sent: AppSync rejects unknown input fields, and a filter
// the server ignores is worse than no filter because it lies about what is in view.
const STATUS_OPTIONS = [
    {
        value: '',
        label: 'All statuses'
    },
    ...Object.values(ScreenshotStatus)
             .map(value => ({
                 value,
                 label: value
             })),
];

export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: Omit<PlatformDrawerProps, 'children'>) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useApplicationScreenshotsFilter();

    // Local draft so the list refetches on Apply, not on every keystroke.
    const [draft, setDraft] = useState<Record<string, string>>(filters);

    // Reopening the drawer shows what is actually applied, including after a
    // sibling component cleared the filters while this was closed.
    //
    // `filters` is deliberately NOT a dependency: including it would overwrite
    // the operator's in-progress edits every time the store changed underneath
    // an open drawer. The sync belongs to the open transition, not to the value.
    useEffect(() => {
        if (opened) {
            setDraft(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync on open only
    }, [opened]);

    const setField = (field: string) => (value: string | null) =>
        setDraft(current => {
            const next = {...current};
            if (value === null || value === '') {
                delete next[field];
            } else {
                next[field] = value;
            }
            return next;
        });

    const handleApply = () => {
        handleSetFilters(draft);
        onClose();
    };

    const handleClear = () => {
        setDraft({});
        handleClearFilters();
        onClose();
    };

    return (
        <PlatformDrawer
            opened={opened}
            onClose={onClose}
            title='Filter Screenshots'
            size='md'
            data-testid='application-screenshots-filter-drawer'
            {...rest}
        >
            <Stack gap='md'>
                <TextInput
                    label='URL'
                    placeholder='Match part of the URL'
                    value={draft.url ?? ''}
                    onChange={(event) => setField('url')(event.currentTarget.value)}
                    data-testid='filter-url'
                />

                <Select
                    label='Status'
                    data={STATUS_OPTIONS}
                    value={draft.status ?? ''}
                    onChange={setField('status')}
                    data-testid='filter-status'
                />

                <Group justify='space-between' mt='md'>
                    <Button
                        variant='subtle'
                        leftSection={<MdClear/>}
                        onClick={handleClear}
                        data-testid='filter-clear'
                    >
                        Clear filters
                    </Button>
                    <Group>
                        <Button
                            variant='default'
                            leftSection={<MdCancel/>}
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            leftSection={<MdCheck/>}
                            onClick={handleApply}
                            data-testid='filter-apply'
                        >
                            Apply
                        </Button>
                    </Group>
                </Group>
            </Stack>
        </PlatformDrawer>
    );
};
